import { formatPriceLocale } from '@pb/utils'

import { PRODUCT_CATEGORY } from '../helpers/constants'
import {
  quantityOfTextLabel,
  colorTextLabel,
  sizeTextLabel,
  quantityTextLabel,
  totalTextLabel,
  returnInProgressLabel,
} from '../helpers/labels'
import { Consignment, Labels, NormalizedOrderItem } from '../types'

export const getProductTableData = ({
  item,
  quantityToReturn,
  labels,
}: {
  item: NormalizedOrderItem['data']
  quantityToReturn?: number
  labels: Labels
}): NormalizedOrderItem['tableData'] => [
  {
    name: labels[colorTextLabel],
    value: item.color,
  },
  {
    name: labels[sizeTextLabel],
    value: item.size,
  },
  ...(item.quantity > 1
    ? [
        {
          name: labels[quantityTextLabel],
          value: quantityToReturn
            ? `${quantityToReturn} ${labels[quantityOfTextLabel]} ${item.quantity}`
            : `${item.quantity}`,
        },
        {
          name: labels[totalTextLabel],
          value: formatPriceLocale({
            amount: item.paidPrice * item.quantity,
            currencyCode: item.currency,
          }),
        },
      ]
    : []),
]

const getOrderItems = (items, consignmentId, labels: Labels): NormalizedOrderItem =>
  items.map((item) => ({
    data: {
      image: item.imageUrl,
      imageAltText: item.name,
      name: item.name,
      price: formatPriceLocale({ amount: item.paidPrice, currencyCode: item.currency }),
      paidPrice: item.paidPrice,
      productId: item.productCode,
      quantity: item.quantity,
      isReturnable: !item.isExternalBrand && !(item.isAlreadyReturned || item.isReturnInProgress),
      isNonReturnable: item.isExternalBrand,
      isReturned: item.isAlreadyReturned || item.isReturnInProgress,
      ...(!item.isAlreadyReturned &&
        item.isReturnInProgress && { returnInProgressText: labels[returnInProgressLabel] }),
      itemId: item.itemId,
      productCode: item.productCode,
      brand: item.brand,
      color: item.color,
      size: item.size,
      currency: item.currency,
      consignmentId,
      markers: [
        {
          type: PRODUCT_CATEGORY,
          text: item.productCategory,
        },
      ],
    },
    tableData: getProductTableData({ item, labels }),
  }))

const getReturnableItems = (items): NormalizedOrderItem =>
  items.filter((item) => item.data.isReturnable)

const getNonReturnableItems = (items): NormalizedOrderItem =>
  items.filter((item) => item.data.isNonReturnable)

const getReturnedItems = (items): NormalizedOrderItem =>
  items.filter((item) => item.data.isReturned)

export const normalizedConsignments = (consignments, labels: Labels): Consignment =>
  consignments.map((consignment) => {
    const { items, consignmentId, warehouse } = consignment
    const orderItems = getOrderItems(items, consignmentId, labels)

    return {
      consignmentId,
      warehouse,
      returnableItems: getReturnableItems(orderItems),
      nonReturnableItems: getNonReturnableItems(orderItems),
      returnedItems: getReturnedItems(orderItems),
    }
  })
